import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Table, TableBody,
  Paper, TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  Button
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';

import AccessLevelDialog from "./AccessLevelDialog/AccessLevelDialog";
import MySnackbar from "./MySnackbar";

import { deleteAdmin, getAdmins, getDeletedAdmins, postAdmin, restoreAdmin, setAdminInternalName } from "../http/clinicAPI";
import { deleteAccess, isOwner } from "../http/accessAPI";
import useSnackbar from "../hooks/useSnackbar";
import { Context } from "..";
import { useNavigate } from 'react-router-dom';
import normalizePhoneNumber from "../helpers/normalizePhoneNumber";

const AdminsSettings = () => {
  const { user } = useContext(Context);
  const [admins, setAdmins] = useState([]);
  const [deletedAdmins, setDeletedAdmins] = useState([]);
  const [phone, setPhone] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [internalNames, setInternalNames] = useState({});
  const navigate = useNavigate();
  const snack = useSnackbar(false, "Ошибка");

  const fetchAdmins = async () => {
    try {
      const data = await getAdmins();
      for (const admin of data) {
        admin.isOwner = await isOwner(admin.id);
      }
      setAdmins(data);
      setDeletedAdmins(await getDeletedAdmins());
      const names = {};
      data.forEach(a => { names[a.id] = a.internalName || ''; });
      setInternalNames(names);
    } catch (error) {
      snack.handleChange(true, "Ошибка при загрузке администраторов", "error");
    }
  };

  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addAdmin = async () => {
    try {
      const temp = admins.find((admin) => admin.phone === normalizePhoneNumber(phone));
      if (temp) {
        snack.handleChange(true, "Администратор уже добавлен");
        return;
      }
      const phoneNormalized = normalizePhoneNumber(phone);
      const data = await postAdmin({ phone: phoneNormalized });
      data.isOwner = false;
      setAdmins([...admins, data]);
      setPhone("");
    } catch (error) {
      snack.handleChange(true, error.response.data.error, "error");
    }
  };

  const removeAdmin = async (id) => {
    try {
      if (!user.user.access.isOwner) return;
      const data = await deleteAdmin({ id });
      if (data.error) throw new Error(data.error);
      await deleteAccess(id);
      setAdmins((current) => current.filter((admin) => admin.id !== id));
      setDeletedAdmins(await getDeletedAdmins());
      snack.handleChange(true, "Администратор удалён", "success");
    } catch (error) {
      snack.handleChange(true, error.message, "error");
    }
  };

  const handleRestoreAdmin = async (id) => {
    try {
      if (!user.user.access.isOwner) return;
      await restoreAdmin({ id });
      await fetchAdmins();
      snack.handleChange(true, "Администратор восстановлен", "success");
    } catch (error) {
      snack.handleChange(true, error.message, "error");
    }
  };

  const handleSaveInternalName = async (adminId) => {
    try {
      await setAdminInternalName(adminId, internalNames[adminId]);
      snack.handleChange(true, "Внутреннее имя сохранено", "success");
      if (adminId === user.user.id) {
        user.setUser({ internalName: internalNames[adminId] || null });
      }
    } catch (error) {
      snack.handleChange(true, "Ошибка при сохранении", "error");
    }
  };

  const handleEditClick = (admin) => {
    setSelectedAdminId(admin.id);
    setOpenDialog(true); // Показываем диалог
  };

  const handleDialogClose = () => {
    setOpenDialog(false); // Закрываем диалог
  };

  return (
    <Box sx={{ maxWidth: 600 }}>
      <MySnackbar {...snack} />
      <Button variant="contained" sx={{mb: '15px', height: '30px'}} onClick={() => navigate('/adminsreferrals')}>Реферальная система администраторов</Button>
      <Typography sx={{mb: '15px'}} component={'pre'}>
        {`Для того, чтобы добавить администратора, введите его номер телефона (тот, который он указал при регистрации в боте) в поле ниже
После добавления администратора нажмите карандаш рядом с его номером, чтобы настроить права доступа в разделах админ.панели
`}
      </Typography>
      <Box sx={{ mb: 1 }}>
        <TextField
          id="outlined-basic"
          label="Номер телефона (89**..)"
          variant="outlined"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <IconButton color="primary" size="large" onClick={addAdmin}>
          <AddCircleOutlineIcon fontSize="inherit" />
        </IconButton>
      </Box>

      <TableContainer component={Paper} elevation={0}>
        <Table sx={{ minWidth: 300 }} aria-label="simple table">
          <TableBody>
            {admins.map((admin) => (
              <TableRow key={admin.id}>
                <TableCell align="left">{admin.name}</TableCell>
                <TableCell align="left">
                  {user.user.access.isOwner ? (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Внутреннее имя"
                        value={internalNames[admin.id] ?? ''}
                        onChange={(e) => setInternalNames(prev => ({ ...prev, [admin.id]: e.target.value }))}
                        sx={{ width: 160 }}
                      />
                      <IconButton size="small" color="primary" onClick={() => handleSaveInternalName(admin.id)}>
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Typography variant="body2">{admin.internalName || ''}</Typography>
                  )}
                </TableCell>
                <TableCell align="right">{admin.phone}</TableCell>
                <TableCell align="right">
                  {admin.isOwner ? "Владелец" : (
                    <IconButton
                      color="primary"
                      aria-label="edit"
                      onClick={() => handleEditClick(admin)}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label="delete"
                    onClick={() => removeAdmin(admin.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {deletedAdmins.length > 0 && (
        <>
          <Typography sx={{ mt: 3, mb: 1 }} variant="subtitle1">
            Удалённые администраторы (корзина)
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table sx={{ minWidth: 300 }} aria-label="deleted admins table">
              <TableBody>
                {deletedAdmins.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell align="left">{admin.name}</TableCell>
                    <TableCell align="left">
                      <Typography variant="body2">{admin.internalName || ''}</Typography>
                    </TableCell>
                    <TableCell align="right">{admin.phone}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        aria-label="restore"
                        onClick={() => handleRestoreAdmin(admin.id)}
                      >
                        <ReplayIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {openDialog && selectedAdminId && (
        <AccessLevelDialog
          adminId={selectedAdminId}
          onClose={handleDialogClose} // Функция для закрытия диалога
        />
      )}
    </Box>
  );
};

export default AdminsSettings;
